// Extracted from BatucadaRio.jsx — WaveEditorTab

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// WAVE EDITOR TAB (Fully Functional with Beat Grid)
// ============================================
export const WaveEditorTab = ({ sample, audioBuffer, settings, selectedKey, onTrimChange, bpm = 120 }) => {
  const canvasRef = useRef(null);
  const [trimStart, setTrimStart] = useState(settings?.trimStart ? (settings.trimStart / (audioBuffer?.duration || 1)) * 100 : 0);
  const [trimEnd, setTrimEnd] = useState(settings?.trimEnd ? (settings.trimEnd / (audioBuffer?.duration || 1)) * 100 : 100);
  const [loopStart, setLoopStart] = useState(settings?.loopStart ? (settings.loopStart / (audioBuffer?.duration || 1)) * 100 : 0);
  const [loopEnd, setLoopEnd] = useState(settings?.loopEnd ? (settings.loopEnd / (audioBuffer?.duration || 1)) * 100 : 100);
  const [isDragging, setIsDragging] = useState(null);
  
  const duration = audioBuffer?.duration || 0;
  const trimStartTime = (trimStart / 100) * duration;
  const trimEndTime = (trimEnd / 100) * duration;
  const loopStartTime = (loopStart / 100) * duration;
  const loopEndTime = (loopEnd / 100) * duration;
  
  // Calculate beat timing
  const secondsPerBeat = 60 / bpm;
  const totalBeats = Math.ceil(duration / secondsPerBeat);
  
  const formatTime = (seconds) => {
    const ms = Math.round((seconds % 1) * 1000);
    const s = Math.floor(seconds);
    return `${s}.${ms.toString().padStart(3, '0')}`;
  };
  
  const memorySizeMB = audioBuffer 
    ? ((audioBuffer.length * audioBuffer.numberOfChannels * 4) / (1024 * 1024)).toFixed(2)
    : '0.00';

  // Draw waveform with beat grid
  useEffect(() => {
    if (!canvasRef.current || !audioBuffer) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);
    
    // Draw beat grid FIRST (behind waveform)
    const pixelsPerSecond = width / duration;
    
    // Draw beat lines (1, 2, 3, 4)
    for (let beat = 0; beat <= totalBeats; beat++) {
      const beatTime = beat * secondsPerBeat;
      const x = beatTime * pixelsPerSecond;
      
      const isBar = beat % 4 === 0; // Every 4 beats is a bar
      
      // Bar lines (brighter)
      if (isBar) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
      } else {
        // Beat lines (dimmer)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
      }
      
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      // Beat number at top
      if (isBar && x < width - 20) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.font = '9px monospace';
        const barNum = Math.floor(beat / 4) + 1;
        ctx.fillText(`${barNum}`, x + 3, 10);
      }
    }
    
    // Draw 16th note subdivisions (very subtle)
    const sixteenthNote = secondsPerBeat / 4;
    for (let i = 0; i < duration / sixteenthNote; i++) {
      if (i % 4 !== 0) { // Skip beats (already drawn)
        const x = i * sixteenthNote * pixelsPerSecond;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }
    
    // Draw waveform
    const channelData = audioBuffer.getChannelData(0);
    const step = Math.ceil(channelData.length / width);
    
    ctx.beginPath();
    ctx.strokeStyle = '#4ade80';
    ctx.lineWidth = 1;
    
    for (let i = 0; i < width; i++) {
      const start = i * step;
      let min = 1.0, max = -1.0;
      
      for (let j = 0; j < step; j++) {
        const datum = channelData[start + j] || 0;
        if (datum < min) min = datum;
        if (datum > max) max = datum;
      }
      
      const yMin = ((1 + min) / 2) * height;
      const yMax = ((1 + max) / 2) * height;
      
      ctx.moveTo(i, yMin);
      ctx.lineTo(i, yMax);
    }
    ctx.stroke();
    
    // Trim regions (darkened)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, (trimStart / 100) * width, height);
    ctx.fillRect((trimEnd / 100) * width, 0, width - (trimEnd / 100) * width, height);
    
    // Loop markers
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    
    const loopStartX = (loopStart / 100) * width;
    ctx.beginPath();
    ctx.moveTo(loopStartX, 0);
    ctx.lineTo(loopStartX, height);
    ctx.stroke();
    
    const loopEndX = (loopEnd / 100) * width;
    ctx.beginPath();
    ctx.moveTo(loopEndX, 0);
    ctx.lineTo(loopEndX, height);
    ctx.stroke();
    
    ctx.setLineDash([]);
    
    // Trim handles
    ctx.fillStyle = '#ef4444';
    const trimStartX = (trimStart / 100) * width;
    const trimEndX = (trimEnd / 100) * width;
    ctx.fillRect(trimStartX - 2, 0, 4, height);
    ctx.fillRect(trimEndX - 2, 0, 4, height);
    
  }, [audioBuffer, trimStart, trimEnd, loopStart, loopEnd, bpm, duration, totalBeats, secondsPerBeat]);

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    
    const handles = [
      { name: 'start', pos: trimStart },
      { name: 'end', pos: trimEnd },
      { name: 'loopStart', pos: loopStart },
      { name: 'loopEnd', pos: loopEnd },
    ];
    
    let closest = null;
    let closestDist = 5;
    
    handles.forEach(h => {
      const dist = Math.abs(h.pos - x);
      if (dist < closestDist) {
        closestDist = dist;
        closest = h.name;
      }
    });
    
    if (closest) {
      setIsDragging(closest);
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    
    switch (isDragging) {
      case 'start':
        setTrimStart(Math.min(x, trimEnd - 1));
        break;
      case 'end':
        setTrimEnd(Math.max(x, trimStart + 1));
        break;
      case 'loopStart':
        setLoopStart(Math.min(x, loopEnd - 1));
        break;
      case 'loopEnd':
        setLoopEnd(Math.max(x, loopStart + 1));
        break;
      default:
        break;
    }
  }, [isDragging, trimStart, trimEnd, loopStart, loopEnd]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      onTrimChange?.(selectedKey, {
        trimStart: trimStartTime,
        trimEnd: trimEndTime,
        loopStart: loopStartTime,
        loopEnd: loopEndTime,
      });
    }
    setIsDragging(null);
  }, [isDragging, selectedKey, trimStartTime, trimEndTime, loopStartTime, loopEndTime, onTrimChange]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!sample || !audioBuffer) {
    return (
      <div className="text-center text-gray-500 py-8">
        No audio loaded for this key
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Info Row */}
      <div className="flex items-center gap-6 text-[10px] text-gray-400">
        <div>
          <span className="text-gray-500">Sample: </span>
          <span className="text-white font-medium">{sample.name}</span>
        </div>
        <div>
          <span className="text-gray-500">Key: </span>
          <span className="text-amber-400 font-medium">{selectedKey}</span>
        </div>
        <div>
          <span className="text-gray-500">Duration: </span>
          <span className="text-white">{formatTime(duration)}s</span>
        </div>
        <div>
          <span className="text-gray-500">Memory: </span>
          <span className="text-white">{memorySizeMB} MB</span>
        </div>
        <div>
          <span className="text-gray-500">Channels: </span>
          <span className="text-white">{audioBuffer?.numberOfChannels || 0}</span>
        </div>
        <div>
          <span className="text-gray-500">Sample Rate: </span>
          <span className="text-white">{audioBuffer?.sampleRate || 0} Hz</span>
        </div>
      </div>
      
      {/* Waveform Display */}
      <canvas
        ref={canvasRef}
        width={600}
        height={80}
        className="w-full h-20 rounded cursor-col-resize bg-[#111]"
        onMouseDown={handleMouseDown}
      />
      
      {/* Controls Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Trim Controls */}
        <div className="bg-[#222] rounded-lg p-3">
          <div className="text-[10px] text-red-400 mb-2 uppercase tracking-wide font-medium">Trim Points</div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-[9px] text-gray-500">Start</label>
              <input
                type="range"
                min="0"
                max="100"
                value={trimStart}
                onChange={(e) => setTrimStart(Number(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded appearance-none cursor-pointer accent-red-500"
              />
              <span className="text-[9px] text-white">{formatTime(trimStartTime)}s</span>
            </div>
            <div className="flex-1">
              <label className="text-[9px] text-gray-500">End</label>
              <input
                type="range"
                min="0"
                max="100"
                value={trimEnd}
                onChange={(e) => setTrimEnd(Number(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded appearance-none cursor-pointer accent-red-500"
              />
              <span className="text-[9px] text-white">{formatTime(trimEndTime)}s</span>
            </div>
          </div>
        </div>
        
        {/* Loop Controls */}
        <div className="bg-[#222] rounded-lg p-3">
          <div className="text-[10px] text-amber-400 mb-2 uppercase tracking-wide font-medium">Loop Points</div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-[9px] text-gray-500">Start</label>
              <input
                type="range"
                min="0"
                max="100"
                value={loopStart}
                onChange={(e) => setLoopStart(Number(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded appearance-none cursor-pointer accent-amber-500"
              />
              <span className="text-[9px] text-white">{formatTime(loopStartTime)}s</span>
            </div>
            <div className="flex-1">
              <label className="text-[9px] text-gray-500">End</label>
              <input
                type="range"
                min="0"
                max="100"
                value={loopEnd}
                onChange={(e) => setLoopEnd(Number(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded appearance-none cursor-pointer accent-amber-500"
              />
              <span className="text-[9px] text-white">{formatTime(loopEndTime)}s</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Apply Button */}
      <div className="flex justify-end">
        <button
          onClick={() => onTrimChange?.(selectedKey, {
            trimStart: trimStartTime,
            trimEnd: trimEndTime,
            loopStart: loopStartTime,
            loopEnd: loopEndTime,
          })}
          className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-medium rounded transition-colors"
        >
          Apply Changes
        </button>
      </div>
    </div>
  );
};
